import React, { useEffect, useState } from "react";
import { useCollection } from "@amzn/awsui-collection-hooks";
import {
  Button,
  Header,
  Pagination,
  SpaceBetween,
  Table,
  Box,
  PropertyFilter,
  PropertyFilterProps,
  TableProps,
} from "@amzn/awsui-components-react";
import {
  FixedFeeConfig,
  FuseAccountingCentralDatastore,
  GetFixedFeeConfigCommand,
  SimplifiedFixedFeeConfig,
} from "@amzn/fuse-accounting-central-datastore-client";
import { NumericValue } from "@smithy/core/serde";
import {
  ARRAY_CELL_WRAPPER,
  FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS,
  FIXED_FEE_CONFIG_ATTRIBUTES,
  FIXED_FEE_CONFIG_FILTERS,
  getFixedFeeConfigTableColDefs,
  LINK_CELL_WRAPPER,
  NUMBER_CELL_WRAPPER,
  STRING_OR_DATE_CELL_WRAPPER,
} from "src/components/allFixedFeeConfigsPage/fixed-fee-config-attributes";
import AdditionalInfoPopover from "src/components/allFixedFeeConfigsPage/components/additional-info-popover";
import { Domain } from "src/config/BartLambdaConfig";

// Pagination labels
const fixedFeeConfigPaginationLabels = {
  nextPageLabel: "Next page",
  pageLabel: (pageNumber: number) => `Go to page ${pageNumber}`,
  previousPageLabel: "Previous page",
};

// Function to get matches count text
function getMatchesCountText(count: number) {
  return count === 1 ? "1 match" : `${count} matches`;
}

// Empty state component
function EmptyState({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle: string;
  action: React.ReactNode;
}) {
  return (
    <Box textAlign="center" color="inherit">
      <Box variant="strong" textAlign="center" color="inherit">
        {title}
      </Box>
      <Box variant="p" padding={{ bottom: "s" }} color="inherit">
        {subtitle}
      </Box>
      {action}
    </Box>
  );
}

interface FixedFeeConfigTableProps {
  searchResults: SimplifiedFixedFeeConfig[];
  facdClient: FuseAccountingCentralDatastore;
  domain: Domain;
}

const FixedFeeConfigTableDisplay: React.FC<FixedFeeConfigTableProps> = ({
  searchResults,
  facdClient,
  domain,
}) => {
  const [allItems, setAllItems] = useState<SimplifiedFixedFeeConfig[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingConfig, setLoadingConfig] = useState<boolean>(false);
  const [configs, setConfigs] = useState<{ [key: string]: FixedFeeConfig }>({});
  // By default, apply the PRODUCTION environment and ENABLED dealStatus filter
  const [query, setQuery] = useState<PropertyFilterProps.Query>({
    tokens: [
      { propertyKey: "environment", operator: "=", value: "PRODUCTION" },
      { propertyKey: "dealStatus", operator: "=", value: "ENABLED" },
    ],
    operation: "and",
  });

  // Non-attribute column for the table
  const FETCH_ADDITIONAL_INFO_CONFIG_COL_DEF: TableProps.ColumnDefinition<SimplifiedFixedFeeConfig> =
    {
      id: "actions",
      header: "Additional Info",
      cell: (item: SimplifiedFixedFeeConfig) => (
        <AdditionalInfoPopover
          fixedFeeConfig={configs[item.fixedFeeId!]}
          loading={loadingConfig}
          child={
            <Button
              onClick={() => {
                if (!(item.fixedFeeId! in configs)) {
                  getFixedFeeConfig(item.fixedFeeId!);
                }
              }}
            >
              Fetch
            </Button>
          }
          domain={domain}
        />
      ),
      minWidth: 120,
    };

  // Default preferences for the table
  const FIXED_FEE_CONFIG_DEFAULT_PREFERENCES = {
    pageSize: 15,
    visibleContent: [
      FETCH_ADDITIONAL_INFO_CONFIG_COL_DEF.id!,
      ...FIXED_FEE_CONFIG_ATTRIBUTES,
    ],
    wrapLines: true,
    custom: "table",
  };

  // Get unique values for property filter
  const getPropertyFilterOptions = (
    propertyKey: string,
  ): PropertyFilterProps.FilteringOption[] => {
    const uniqueValues = new Set<string>();
    allItems.forEach((item) => {
      const { stringCell, linkCell, numberCell, arrayCell } =
        FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS[propertyKey];
      let itemValues: string[] | undefined;
      if (stringCell) {
        itemValues = [STRING_OR_DATE_CELL_WRAPPER(stringCell(item))];
      } else if (linkCell) {
        itemValues = [LINK_CELL_WRAPPER(linkCell(item, domain))];
      } else if (numberCell) {
        itemValues = [NUMBER_CELL_WRAPPER(numberCell(item))];
      } else if (arrayCell) {
        itemValues = arrayCell(item);
      }

      itemValues?.forEach((itemValue: string) => {
        if (itemValue) {
          uniqueValues.add(itemValue);
        }
      });
    });
    return Array.from(uniqueValues)
      .sort()
      .map((value) => ({ propertyKey, value }));
  };

  useEffect(() => {
    // Focus is now handled by the Input component
  }, []);

  useEffect(() => {
    if (Array.isArray(searchResults)) {
      setAllItems(searchResults);
      setLoading(false);
    } else {
      setAllItems([]);
      setLoading(searchResults === "init");
    }
  }, [searchResults]);

  const getFixedFeeConfig = async (fixedFeeId: string) => {
    setLoadingConfig(true);

    try {
      const getFixedFeeConfigCommand = new GetFixedFeeConfigCommand({
        fixedFeeId,
      });

      console.log(
        "Starting FACD getFixedFeeConfig operation with getFixedFeeConfigCommand:",
        getFixedFeeConfigCommand,
      );

      const response = await facdClient.send(getFixedFeeConfigCommand);
      console.log("Response received from facdClient:", response);
      let fixedFeeConfig: FixedFeeConfig = response.fixedFeeConfig!;

      setConfigs({ ...configs, [fixedFeeId]: fixedFeeConfig });
      setLoadingConfig(false);
      console.log(Object.keys(configs));
    } catch (error) {
      console.error("An error occurred in getFixedFeeConfig:", error);
      setLoadingConfig(false);
    } finally {
      console.log("Get FixedFeeConfig operation completed.");
    }
  };

  // Property filter matching function
  const matchesPropertyFilter = (
    item: SimplifiedFixedFeeConfig,
    tokens: readonly PropertyFilterProps.Token[],
  ): boolean => {
    return tokens.every((token) => {
      const { propertyKey, operator, value } = token;
      if (!propertyKey) {
        return false;
      }

      const { stringCell, dateCell, linkCell, numberCell, arrayCell } =
        FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS[propertyKey];
      let stringValue: string | undefined;
      if (stringCell) {
        stringValue = STRING_OR_DATE_CELL_WRAPPER(stringCell(item));
      } else if (dateCell) {
        stringValue = STRING_OR_DATE_CELL_WRAPPER(dateCell(item));
      } else if (linkCell) {
        stringValue = LINK_CELL_WRAPPER(linkCell(item, domain));
      } else if (numberCell) {
        const numericValue: NumericValue | undefined = numberCell(item);
        if (!numericValue) {
          return false;
        }

        const numberValue = Number(numericValue);
        const targetNumberValue = Number(parseInt(value.replace(/,/g, "")));

        switch (operator) {
          case "<":
            return numberValue < targetNumberValue;
          case "<=":
            return numberValue <= targetNumberValue;
          case ">":
            return numberValue > targetNumberValue;
          case ">=":
            return numberValue >= targetNumberValue;
        }
        stringValue = NUMBER_CELL_WRAPPER(numericValue);
      } else if (arrayCell) {
        stringValue = ARRAY_CELL_WRAPPER(arrayCell(item));
      }

      switch (operator) {
        case "=":
          return stringValue === value;
        case "!=":
          return stringValue !== value;
        case "<":
          return stringValue! < value;
        case "<=":
          return stringValue! <= value;
        case ">":
          return stringValue! > value;
        case ">=":
          return stringValue! >= value;
        case ":":
          return stringValue!.includes(value);
        case "!:":
          return !stringValue!.includes(value);
        default:
          return false;
      }
    });
  };

  const clearFilter = () => {
    setQuery({ tokens: [], operation: "and" });
    actions.setFiltering("");
  };

  const {
    items,
    actions,
    filteredItemsCount,
    collectionProps,
    filterProps,
    paginationProps,
  } = useCollection(allItems, {
    filtering: {
      empty: (
        <EmptyState
          title="No FixedFee Configs"
          subtitle="No FixedFee Configs to display."
          action={null}
        />
      ),
      noMatch: (
        <EmptyState
          title="No matches"
          subtitle="We can't find a match."
          action={<Button onClick={clearFilter}>Clear all filters</Button>}
        />
      ),
      filteringFunction: (item, filteringText) => {
        // Handle property filter tokens
        if (
          query.tokens.length > 0 &&
          !matchesPropertyFilter(item, query.tokens)
        ) {
          return false;
        }

        // Handle free text search
        if (filteringText) {
          const filteringTextLowerCase = filteringText.toLowerCase();
          return FIXED_FEE_CONFIG_ATTRIBUTES.some((field) => {
            const { stringCell, dateCell, linkCell, numberCell, arrayCell } =
              FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS[field];
            let itemValue;
            if (stringCell) {
              itemValue = STRING_OR_DATE_CELL_WRAPPER(stringCell(item));
            } else if (dateCell) {
              itemValue = STRING_OR_DATE_CELL_WRAPPER(dateCell(item));
            } else if (linkCell) {
              itemValue = LINK_CELL_WRAPPER(linkCell(item, domain));
            } else if (numberCell) {
              itemValue = NUMBER_CELL_WRAPPER(numberCell(item));
            } else if (arrayCell) {
              itemValue = ARRAY_CELL_WRAPPER(arrayCell(item));
            }
            return (
              itemValue?.toLowerCase()?.includes(filteringTextLowerCase) ??
              false
            );
          });
        }

        return true;
      },
    },
    pagination: {
      pageSize: FIXED_FEE_CONFIG_DEFAULT_PREFERENCES.pageSize,
    },
    sorting: {},
    selection: {},
  });

  return (
    <SpaceBetween size="m">
      <Table
        {...collectionProps}
        visibleColumns={FIXED_FEE_CONFIG_DEFAULT_PREFERENCES.visibleContent}
        wrapLines={FIXED_FEE_CONFIG_DEFAULT_PREFERENCES.wrapLines}
        loading={loading}
        loadingText="Loading FixedFeeConfigs"
        stickyHeaderVerticalOffset={1}
        variant="full-page"
        header={
          <Header
            variant="awsui-h1-sticky"
            actions={null}
            counter={`(${allItems.length})`}
          >
            FixedFee Configs
          </Header>
        }
        columnDefinitions={[
          FETCH_ADDITIONAL_INFO_CONFIG_COL_DEF,
          ...getFixedFeeConfigTableColDefs(domain),
        ]}
        items={items}
        pagination={
          <Pagination
            {...paginationProps}
            ariaLabels={fixedFeeConfigPaginationLabels}
          />
        }
        filter={
          <PropertyFilter
            {...filterProps}
            query={query}
            onChange={({ detail }) => setQuery(detail)}
            filteringProperties={FIXED_FEE_CONFIG_FILTERS}
            filteringOptions={FIXED_FEE_CONFIG_FILTERS.reduce<
              PropertyFilterProps.FilteringOption[]
            >(
              (acc, property) =>
                acc.concat(getPropertyFilterOptions(property.key)),
              [],
            )}
            countText={getMatchesCountText(filteredItemsCount ?? 0)}
            expandToViewport={true}
            filteringAriaLabel="Find FixedFee Configs"
            filteringPlaceholder="Find FixedFee Configs"
            i18nStrings={{
              filteringPlaceholder: "Find APIs",
              operationAndText: "and",
              operationOrText: "or",
              clearFiltersText: "Clear filters",
              operatorContainsText: "Contains",
              operatorDoesNotContainText: "Does not contain",
              operatorEqualsText: "Equals",
              operatorDoesNotEqualText: "Does not equal",
              applyActionText: "Apply",
            }}
          />
        }
      />
    </SpaceBetween>
  );
};

export default FixedFeeConfigTableDisplay;
